import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

export const useAudioStore = defineStore('audio', () => {
  // 当前播放的音频ID
  const currentAudioId = ref<number | string | null>(null);
  // 是否正在播放
  const isPlaying = ref<boolean>(false);
  // 新增：音频加载状态
  const isLoading = ref<boolean>(false);
  // 新增：当前播放时间(秒)
  const currentTime = ref<number>(0);
  // 新增：音频总时长(秒)
  const duration = ref<number>(0);
  let audioInstance: HTMLAudioElement | null = null;

  // 新增：播放进度百分比
  const percent = computed(() => {
    if (!duration.value || isNaN(duration.value)) return 0;
    return Math.min(Math.max((currentTime.value / duration.value) * 100, 0), 100);
  });

  /**
   * 播放指定ID的音频，暂停当前正在播放的音频
   * @param id 音频唯一标识
   * @param src 音频资源路径
   */
  const playAudio = async (id: string | number, src: string): Promise<void> => {
    // 如果有正在播放的音频，先暂停
    if (audioInstance) {
      audioInstance.pause();
    }

    // 如果是同一音频且已有实例，直接播放
    if (currentAudioId.value === id && audioInstance) {
      await audioInstance.play();
      isPlaying.value = true;
      return;
    }

    // 新增：设置加载状态
    isLoading.value = true;
    currentTime.value = 0;
    duration.value = 0;

    // 创建新的音频实例
    const audio = new Audio(src);
    audioInstance = audio;
    currentAudioId.value = id;
    // 新增：监听元数据加载完成事件获取总时长
    audio.addEventListener('loadedmetadata', () => {
      duration.value = audio.duration || 0;
    });

    // 新增：监听播放进度更新事件
    audio.addEventListener('timeupdate', () => {
      currentTime.value = audio.currentTime || 0;
    });
    try {
      await audio.play();
      isPlaying.value = true;
      // 监听音频结束事件，重置状态
      audio.onended = () => {
        isPlaying.value = false;
        currentAudioId.value = null;
        currentTime.value = 0;
        duration.value = 0;
        audioInstance = null;
      };
    } catch (error) {
      console.error('音频播放失败:', error);
      // 播放失败时重置状态
      isPlaying.value = false;
      currentAudioId.value = null;
      currentTime.value = 0;
      duration.value = 0;
      audioInstance = null;
    } finally {
      // 新增：加载完成后重置状态
      isLoading.value = false;
    }
  };

  /**
   * 暂停当前播放的音频
   */
  const pauseAudio = (): void => {
    if (audioInstance) {
      audioInstance.pause();
      isPlaying.value = false;
    }
  };

  /**
   * 切换音频播放状态（播放/暂停）
   * @param id 音频唯一标识
   * @param src 音频资源路径
   */
  const togglePlay = (id: string | number, src: string): void => {
    if (currentAudioId.value === id && isPlaying.value) {
      pauseAudio();
    } else {
      playAudio(id, src);
    }
  };

  /**
   * 停止当前播放的音频并重置所有状态
   */
  const stopAudio = (): void => {
    if (audioInstance) {
      audioInstance.pause();
      audioInstance = null;
    }
    isPlaying.value = false;
    currentAudioId.value = null;
    currentTime.value = 0;
    duration.value = 0;
  };

  return {
    currentAudioId,
    isPlaying,
    isLoading,
    currentTime,
    duration,
    percent,
    playAudio,
    pauseAudio,
    togglePlay,
    stopAudio,
  };
});
